import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface StatusOption {
  value: string;
  label: string;
  color?: string;
}

interface BulkActionBarProps {
  selectedCount: number;
  onStatusChange: (status: string) => void;
  onExport: () => void;
  onDelete: () => void;
  onClearSelection: () => void;
  isProcessing?: boolean;
  exportLabel?: string;
  statusOptions?: StatusOption[];
  itemType?: string;
}

const defaultStatusOptions: StatusOption[] = [
  { value: 'new', label: 'New', color: 'text-blue-600' },
  { value: 'contacted', label: 'Contacted', color: 'text-zinc-700' },
  { value: 'qualified', label: 'Qualified', color: 'text-purple-600' },
  { value: 'converted', label: 'Converted', color: 'text-green-600' },
  { value: 'lost', label: 'Lost', color: 'text-red-600' },
];

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onStatusChange,
  onExport,
  onDelete,
  onClearSelection,
  isProcessing = false,
  exportLabel = 'Export',
  statusOptions = defaultStatusOptions,
  itemType = 'leads',
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  return (
    <AnimatePresence>
      {selectedCount > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4"
        >
          <div className="bg-zinc-900 text-white rounded-xl shadow-2xl border border-zinc-700 px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
            {/* Selected count */}
            <div className="flex items-center gap-2 pr-2 sm:pr-3 border-r border-zinc-700">
              <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center text-xs font-bold text-white">
                {selectedCount}
              </div>
              <span className="text-sm font-medium text-zinc-300 hidden sm:inline">
                {selectedCount === 1 ? itemType.replace(/s$/, '') : itemType} selected
              </span>
            </div>

            {/* Change Status Dropdown */}
            <div className="relative">
              <motion.button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                disabled={isProcessing}
                whileHover={{ scale: isProcessing ? 1 : 1.02 }}
                whileTap={{ scale: isProcessing ? 1 : 0.98 }}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs sm:text-sm font-medium transition-colors disabled:opacity-50"
              >
                <StatusIcon />
                <span className="hidden sm:inline">Change Status</span>
                <span className="sm:hidden">Status</span>
                <ChevronDownIcon />
              </motion.button>

              <AnimatePresence>
                {showStatusDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowStatusDropdown(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full left-0 mb-2 bg-white rounded-xl shadow-xl border border-zinc-200 overflow-hidden z-20 min-w-[160px]"
                    >
                      {statusOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onStatusChange(option.value);
                            setShowStatusDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-100 transition-colors flex items-center gap-2"
                        >
                          {option.color && <span className={`w-2 h-2 rounded-full ${option.color.replace('text-', 'bg-')}`} />}
                          <span className={option.color || 'text-primary'}>{option.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* Export Button */}
            <motion.button
              onClick={onExport}
              disabled={isProcessing}
              whileHover={{ scale: isProcessing ? 1 : 1.02 }}
              whileTap={{ scale: isProcessing ? 1 : 0.98 }}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs sm:text-sm font-medium transition-colors disabled:opacity-50"
              title="Export selected leads"
            >
              <ExportIcon />
              <span className="hidden sm:inline">{exportLabel}</span>
            </motion.button>

            {/* Delete Button */}
            <motion.button
              onClick={onDelete}
              disabled={isProcessing}
              whileHover={{ scale: isProcessing ? 1 : 1.02 }}
              whileTap={{ scale: isProcessing ? 1 : 0.98 }}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-xs sm:text-sm font-medium transition-colors disabled:opacity-50"
              title="Delete selected leads"
            >
              <TrashIcon />
              <span className="hidden sm:inline">Delete</span>
            </motion.button>

            {/* Clear Selection */}
            <motion.button
              onClick={onClearSelection}
              disabled={isProcessing}
              whileHover={{ scale: isProcessing ? 1 : 1.02 }}
              whileTap={{ scale: isProcessing ? 1 : 0.98 }}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-colors disabled:opacity-50"
              title="Clear selection"
            >
              <XIcon />
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Icons
const StatusIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const ChevronDownIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const ExportIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const XIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

// Delete Confirmation Modal Component
interface DeleteConfirmModalProps {
  isOpen: boolean;
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
  itemType?: string;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  count,
  onConfirm,
  onCancel,
  isDeleting = false,
  itemType = 'Lead',
}) => {
  const singularItem = itemType.replace(/s$/, '');
  const pluralItem = itemType.endsWith('s') ? itemType : itemType + 's';
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onCancel}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-xl shadow-2xl border border-zinc-200 p-6 max-w-md w-full"
          >
            {/* Warning Icon */}
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-display font-semibold text-primary text-center mb-2">
              Delete {count} {count === 1 ? singularItem : pluralItem}?
            </h3>

            {/* Message */}
            <p className="text-muted text-center mb-6">
              Are you sure you want to delete {count} {count === 1 ? singularItem.toLowerCase() : pluralItem.toLowerCase()}? This action cannot be undone.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <motion.button
                onClick={onCancel}
                disabled={isDeleting}
                whileHover={{ scale: isDeleting ? 1 : 1.02 }}
                whileTap={{ scale: isDeleting ? 1 : 0.98 }}
                className="flex-1 px-4 py-2.5 border border-zinc-200 text-primary rounded-lg hover:bg-zinc-100 transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={onConfirm}
                disabled={isDeleting}
                whileHover={{ scale: isDeleting ? 1 : 1.02 }}
                whileTap={{ scale: isDeleting ? 1 : 0.98 }}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BulkActionBar;
